import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLogoForStyle } from '../utils/styleLogos';
import { resolvePostMedia } from '../utils/postMedia';
import useVideoProgressBar from '../hooks/useVideoProgressBar';
import VideoSlideOverlay from './VideoSlideOverlay';
import StableAvatar from './StableAvatar';
import './PostCard.css';

const getMediaLabel = (role, t) => {
  if (role === 'video') return t('postCard.transitionVideo', 'Transition video');
  if (role === 'original') return t('postCard.originalStravaMap', 'Original Strava map');
  return t('postCard.mythicMap', 'Mythic map');
};

const stripFirstEmoji = (text) => {
  if (!text) return text;
  // Remove first emoji character(s) - handles most emoji ranges and sequences
  // This regex matches common emoji patterns at the start of the string
  return text.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]+[\u{200D}\u{FE0F}]?/u, '').trim();
};

const PostCard = ({ post, user, onLoginRequired, navigateOnImageClick = false, templateLogos = {} }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const media = resolvePostMedia(post);
  const currentMediaItem = media[currentImageIndex] || null;
  const {
    isCurrentVideoReady,
    progressFillRef,
    updateVideoProgress,
    videoRefs,
  } = useVideoProgressBar(
    currentImageIndex,
    currentMediaItem?.kind === 'video'
  );

  useEffect(() => {
    if (currentImageIndex >= media.length && media.length > 0) {
      setCurrentImageIndex(0);
    }
  }, [currentImageIndex, media.length]);

  // Resolve style logo: try exact styleName, then base name (without 3D suffix), then post.logoUrl
  const styleBaseName = post.styleName?.replace(/ 3D$/, '').replace(/3D$/, '').trim();
  const dynamicLogoUrl = templateLogos[post.styleName] || templateLogos[styleBaseName] || post.logoUrl;
  const styleLogo = getLogoForStyle(post.styleName, dynamicLogoUrl);

  const handleProfileClick = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    const targetUid = post.user.id || post.user.userId || post.user.uid;
    if (targetUid) {
      navigate(`/profile/${targetUid}`);
    }
  };

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'object' && value._seconds !== undefined) {
      const seconds = Number(value._seconds) || 0;
      const nanos = Number(value._nanoseconds || value.nanoseconds || 0);
      return new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : new Date(parsed);
  };

  const formatTimeAgo = (dateInput) => {
    const date = parseDate(dateInput);
    if (!date) return '';

    const now = Date.now();
    const diffInSeconds = Math.floor((now - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds || isNaN(totalSeconds)) return 'N/A';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatPace = (avgSpeed) => {
    if (!avgSpeed || avgSpeed <= 0) return null;
    const secondsPerKm = 1000 / avgSpeed;
    const minutes = Math.floor(secondsPerKm / 60);
    const seconds = Math.round(secondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')} /km`;
  };

  const shouldShowPower = post.activityType !== 'run' && post.stravaStats?.averageWatts;
  const runPace = post.activityType === 'run' ? formatPace(post.stravaStats?.averageSpeed || post.stravaStats?.average_speed) : null;

  return (
    <article className="post-card">
      {/* Image/Media Carousel - stacked images for instant switching */}
      <div className="post-media">
        <div
          className="post-image-stack"
          onClick={() => {
            if (navigateOnImageClick) {
              if (!user) {
                if (onLoginRequired) {
                  onLoginRequired();
                }
                return;
              }
              navigate(`/post/${post.id}`);
            } else if (media.length > 1) {
              setCurrentImageIndex((prev) => (prev + 1) % media.length);
            }
          }}
        >
          {media.map((item, index) => {
            const isActive = index === currentImageIndex;
            // Portrait class drives video sizing (no-op for <img>, which sizes
            // by natural aspect ratio). Video items carry their own orientation
            // from the backend so the home feed works even when the post-level
            // mapOrientation isn't surfaced through publicFeed.
            const isPortrait = item.kind === 'video' ?
              item.orientation === 'portrait' :
              post.mapOrientation === 'portrait';
            const layerClass = `post-image stack-layer ${isActive ? 'active' : 'inactive'} ${isPortrait ? 'portrait' : ''}`;
            if (item.kind === 'video') {
              return (
                <video
                  key={`${post.id}-vid-${index}`}
                  ref={(el) => { videoRefs.current[index] = el; }}
                  src={item.uri}
                  className={layerClass}
                  playsInline
                  muted
                  loop
                  preload="auto"
                  onDurationChange={(event) => updateVideoProgress(event.currentTarget)}
                  onLoadedMetadata={(event) => updateVideoProgress(event.currentTarget)}
                  onTimeUpdate={(event) => updateVideoProgress(event.currentTarget)}
                />
              );
            }
            return (
              <img
                key={`${post.id}-img-${index}`}
                src={item.uri}
                alt={getMediaLabel(item.role, t)}
                className={layerClass}
                loading={index === 0 ? 'eager' : 'auto'}
                decoding="async"
                draggable={false}
              />
            );
          })}
        </div>

        {currentMediaItem?.kind === 'video' && (
          <VideoSlideOverlay
            key={`${post.id}-video-overlay-${currentImageIndex}`}
            label={t('postCard.videoBadge', 'Video')}
            loadingLabel={t('postCard.loadingVideo', 'Loading video')}
            isLoading={!isCurrentVideoReady}
            progressFillRef={progressFillRef}
          />
        )}

        {/* View on Strava link for original map */}
        {currentMediaItem?.role === 'original' && post.stravaActivityId && (
          <a
            href={`https://www.strava.com/activities/${post.stravaActivityId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="strava-overlay-link"
            onClick={(e) => e.stopPropagation()}
          >
            {t('postCard.viewOnStrava', 'View on Strava')}
          </a>
        )}

        {/* Image navigation dots */}
        <div className="image-dots">
          {media.map((item, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(index);
              }}
              className={`image-dot ${index === currentImageIndex ? 'active' : ''}`}
              aria-label={getMediaLabel(item.role, t)}
            />
          ))}
        </div>
      </div>

      {/* Actions and User Info */}
      <div className="post-actions">
        {/* User Info and Action Buttons - Same Line */}
        <div className="post-header-bottom">
          {/* User Info - Left */}
          <div className="post-user-info-bottom">
            <div className="user-avatar-small" onClick={handleProfileClick}>
              <StableAvatar
                identityKey={post.user.id || post.user.userId || post.user.uid}
                src={post.user.avatar}
                alt={post.user.username}
              />
            </div>
            <div className="user-details-bottom">
              <span className="username" onClick={handleProfileClick}>
                {post.user.username}
              </span>
              {post.location && (
                <span className="location-small">{post.location}</span>
              )}
            </div>
          </div>
        </div>

        {/* Caption with Activity Icon and Stats */}
        <div className="post-caption">
          <span className="caption-text">
            {post.caption}
            {post.stravaStats && (
              <>
                {post.stravaStats.distance ? ` ${t('postCard.distance', 'Distance')}: ${(post.stravaStats.distance / 1000).toFixed(1)} km` : ''}
                {post.stravaStats.movingTime ? ` | ${t('postCard.time', 'Time')}: ${formatDuration(post.stravaStats.movingTime)}` : ''}
                {shouldShowPower ? ` | ${t('postCard.power', 'Power')}: ${Math.round(post.stravaStats.averageWatts)} W` : ''}
                {runPace ? ` | ${t('postCard.pace', 'Pace')}: ${runPace}` : ''}
                {post.stravaStats.elevationGain ? ` | ${t('postCard.elevation', 'Elevation')}: ${Math.round(post.stravaStats.elevationGain)} m` : ''}
              </>
            )}
          </span>
          {post.activityType && (
            <div className="activity-icon">
              {(post.activityType === 'ride' || post.activityType === 'virtualride') && '🚴'}
              {post.activityType === 'run' && '🏃'}
              {post.activityType === 'walk' && '🚶'}
              {post.activityType === 'hike' && '🥾'}
              {post.activityType === 'swim' && '🏊'}
              {post.activityType === 'sailing' && '⛵'}
              {post.activityType === 'windsurfing' && '🏄'}
              {post.activityType === 'kayak' && '🛶'}
            </div>
          )}
        </div>

        {/* Style and Time */}
        <div className="post-footer-bottom">
          {/* Style - Left */}
          {post.styleName && (
            <div className="post-style">
              {styleLogo && (
                <img
                  src={styleLogo}
                  alt={post.styleName}
                  className="post-style-logo"
                  title={post.styleName}
                />
              )}
              <span className="post-style-name">{stripFirstEmoji(post.styleName)}</span>
            </div>
          )}

          {/* Time - Right */}
          <div className="post-time">
            {formatTimeAgo(post.createdAt)}{formatTimeAgo(post.createdAt) ? ` ${t('postCard.ago', 'ago')}` : ''}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PostCard;
